import { Job } from "../shared-types";

interface JobCardProps {
  job: Job;
  compact?: boolean;
  onAssign?: () => void;
  onAccept?: () => void;
  onComplete?: () => void;
  onCancel?: () => void;
}

function JobCard({ job, compact, onAssign, onAccept, onComplete, onCancel }: JobCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'assigned':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
      <div className="p-5">
        <div className="flex justify-between mb-2">
          <h3 className="text-lg font-bold truncate">{job.title}</h3>
          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(job.status)}`}>
            {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
          </span>
        </div>

        {!compact && (
          <p className="text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{job.description}</p>
        )}

        <div className="flex flex-col text-sm space-y-1 mb-3">
          <div className="flex">
            <span className="text-gray-500 dark:text-gray-400 w-24">Location:</span>
            <span className="text-gray-700 dark:text-gray-300 truncate">{job.pickup_location}</span>
          </div>
          <div className="flex">
            <span className="text-gray-500 dark:text-gray-400 w-24">Start Time:</span>
            <span className="text-gray-700 dark:text-gray-300">
              {new Date(job.pickup_time).toLocaleString()}
            </span>
          </div>
          <div className="flex">
            <span className="text-gray-500 dark:text-gray-400 w-24">License:</span>
            <span className="text-gray-700 dark:text-gray-300">{job.license_class}</span>
          </div>
          <div className="flex">
            <span className="text-gray-500 dark:text-gray-400 w-24">Pay Rate:</span>
            <span className="text-gray-700 dark:text-gray-300">${job.pay_rate}/hr</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {onAssign && job.status === 'pending' && (
            <button
              onClick={onAssign}
              className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
            >
              Assign Driver
            </button>
          )}
          {onAccept && job.status === 'pending' && (
            <button
              onClick={onAccept}
              className="px-3 py-1 bg-green-500 text-white rounded-md text-sm hover:bg-green-600"
            >
              Accept Job
            </button>
          )}
          {onComplete && job.status === 'assigned' && (
            <button
              onClick={onComplete}
              className="px-3 py-1 bg-green-500 text-white rounded-md text-sm hover:bg-green-600"
            >
              Complete
            </button>
          )}
          {onCancel && (job.status === 'pending' || job.status === 'assigned') && (
            <button
              onClick={onCancel}
              className="px-3 py-1 bg-gray-500 text-white rounded-md text-sm hover:bg-gray-600"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface JobListProps {
  jobs: Job[];
  isLoading: boolean;
  error: Error | null;
  onAssignJob?: (jobId: string) => void;
  onAcceptJob?: (jobId: string) => void;
  onCompleteJob?: (jobId: string) => void;
  onCancelJob?: (jobId: string) => void;
  emptyMessage?: string;
  compact?: boolean;
  className?: string;
}

export function JobList({
  jobs,
  isLoading,
  error,
  onAssignJob,
  onAcceptJob,
  onCompleteJob,
  onCancelJob,
  emptyMessage = "No jobs found",
  compact = false,
  className = "",
}: JobListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg text-red-700 dark:text-red-300">
        <p className="font-medium">Error loading jobs</p>
        <p className="text-sm">{error.message}</p>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 p-8 rounded-lg text-center text-gray-500 dark:text-gray-400">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${className}`}>
      {jobs.map((job) => (
        <JobCard
          key={job.id}
          job={job}
          compact={compact}
          onAssign={onAssignJob ? () => onAssignJob(job.id) : undefined}
          onAccept={onAcceptJob ? () => onAcceptJob(job.id) : undefined}
          onComplete={onCompleteJob ? () => onCompleteJob(job.id) : undefined}
          onCancel={onCancelJob ? () => onCancelJob(job.id) : undefined}
        />
      ))}
    </div>
  );
}
